'use strict';

import get from 'lodash-es/get';
import findIndex from 'lodash-es/findIndex';
import MlbButton from './MlbButton';
import MlbButtonGroup from './MlbButtonGroup';

import ButtonConfigs from './buttonConfigs';
import { formatInterruptionDate, dictionaryAndReplace } from './MlbGameUtils/helpers';

/**
 * Base GameUtils class.  Returns team-homepage links, player-page links, and generic
 * links.  This GameUtils class must work for BANE pages which live
 * within the context of a larger MLB.com, all of which has not yet been converted to BANE.
 */
class GameUtils {

    /**
     *
     * @param {object} ctx Either a BANE browser window or a BANE-controller "req"; optional (see above)
     * @param {string} callerCode Which UI element is instantiating this GameUtils; accepted values are the
     *   CALLER_CODE_* instance constants in this class. Required.
     */
    constructor(opts) {

        this._dictionaryData = opts.dictionaryData;
        this._callerCode = opts.callerCode;
        this._linker = opts.linker;
        this._moment = opts.moment;
        this.CODE_TENSE_BUTTONS = opts.codeTenseButtons;
        this._buttonGroup = opts.buttonGroup;

    }

    /**
     * Get a translation from the dictionary
     * @param key
     * @param defaultVal
     * @return {string} The translation if present, or the defaultVal if present, or the key
     * @protected
     */
    _getTrans(key, defaultVal) {
        return (this._dictionaryData[key] || defaultVal || key);
    }

    /**
     * Returns 'past', 'present', or 'future' depending on game status
     * @param game
     * @returns  'past', 'present', or 'future'
     * @private

     */
    getGameTense(game) {

        let status = game.status.abstractGameCode;

        if (status === 'F') {
            return 'past';
        }
        if (status === 'P') {
            return 'future';
        }
        return 'present';
    }

    /**
     * Returns all of the buttons which may be displayed for this game, depending
     * on whether the game is ongoing, future, or past; whether there's media available or not; etc.
     * @param {object} game A statsapi game node
     * @returns {object} a JS object with keys such as 'recap', 'watch', etc; values are MlbButton instances
     */
    getButtons(game) {
        let gameTense = this.getGameTense(game);

        let buttons = this.CODE_TENSE_BUTTONS[this._callerCode][gameTense];

        let buttonGroup = this._buttonGroup ? new this._buttonGroup() : new MlbButtonGroup();

        buttons.forEach((button) => {

            let buttonDetails = ButtonConfigs[button];

            let { linkerFunction } = buttonDetails;

            let url = this._linker[linkerFunction](game);

            if (url) {

                let { text, glyph, target, type, accessibility } = buttonDetails;
                let bText = this._getTrans(text.dictionary, text.fallback);
                let bAriaLabel = this._getTrans(accessibility.dictionary, accessibility.fallback);

                buttonGroup.addButton(type, new MlbButton(url, bText, glyph, target, { buttonType: type }, bAriaLabel));
            }
        });

        return buttonGroup;
    }

    /**
     * Returns localized strings to be displayed in the case of games which were postponed, suspended, or cancelled.
     * These messages may apply to:
     * <ul>
     *  <li>Game which was postponed and will be played on some future date (which may or may not be known)</li>
     *  <li>Game which is the makeup for a postponed game (see previous item)</li>
     *  <li>Game which was suspended and will be completed on some future date (which may or may not be known)</li>
     *  <li>Game which is the continuation of a suspended game (see previous item)</li>
     *  <li>Game which was cancelled</li>
     * </ul>
     * Passed a game which is not one of the five types above, this method will return null.
     *
     * Returned value is an object with two properties, both strings: "primary" (a string like "Postponed: rain"),
     * which will never be null, and "makeup" (e.g. "Makeup on 7/23" or "Completed on {{date}}"), which in the case
     * of postponed and suspended games may be null if the date of the makeup game is not yet known, and which will
     * always be null for cancelled games (since they don't have a makeup game, by definition). These two strings may
     * be concatenated (after a null check for "makeup") to suit your needs, resulting e.g. in strings like
     * "Postponed: rain. Makeup on 7/23" or "Postponed: rain<br/>Makeup on 7/23"
     *
     * This method uses these dictionary keys: game_canc_reason; game_ppd_reason; game_susp_reason;
     * game_ppd_makeup_date; game_ppd_makeup_of; game_susp_to_be_completed; game_susp_completed_on_date.
     *
     * @param statsapiGame
     * @return {{primary: string, makeup: string} | null}
     * @todo Use `status.detailedState` once data is localized (SSSS-4106 which depends on SDBBD-2130)
     */
    getInterruptedGameStrs(statsapiGame) {
        const { rescheduleDate, rescheduledFrom, resumeDate, resumedFrom } = statsapiGame;
        const extendedStatus = this.getExtendedStatus(statsapiGame);
        const translateFunction = this._getTrans.bind(this);
        let primary = null;
        let makeup = null;
        let reason = '';

        if (statsapiGame.status && statsapiGame.status.reason) {

            reason = statsapiGame.status.reason;

        }

        const { isPreview, isPostponed, isCancelled, isDelayed, isSuspended } = extendedStatus;

        if (isSuspended) {

            primary = dictionaryAndReplace({
                translateFunction,
                dictionaryKey: 'game_susp_reason',
                dictionaryFallback: 'Suspended: {{reason}}',
                replaceKey: '{{reason}}',
                replaceValue: reason
            });

            if (resumeDate) {

                makeup = dictionaryAndReplace({
                    translateFunction,
                    dictionaryKey: 'game_susp_to_be_completed',
                    dictionaryFallback: 'To be completed on {{date}}',
                    replaceKey: '{{date}}',
                    replaceValue: formatInterruptionDate(resumeDate, this._moment)
                });

            } else if (resumedFrom) {

                makeup = dictionaryAndReplace({
                    translateFunction,
                    dictionaryKey: 'game_susp_completion_on_date',
                    dictionaryFallback: 'Completion of game suspended on {{date}}',
                    replaceKey: '{{date}}',
                    replaceValue: formatInterruptionDate(resumedFrom, this._moment)
                });

            }

        } else if (isPostponed) {

            primary = dictionaryAndReplace({
                translateFunction,
                dictionaryKey: 'game_ppd_reason',
                dictionaryFallback: 'Postponed: {{reason}}',
                replaceKey: '{{reason}}',
                replaceValue: reason
            });

            if (rescheduleDate) {

                makeup = dictionaryAndReplace({
                    translateFunction,
                    dictionaryKey: 'game_ppd_makeup_date',
                    dictionaryFallback: 'Makeup on {{date}}',
                    replaceKey: '{{date}}',
                    replaceValue: formatInterruptionDate(rescheduleDate, this._moment)
                });

            }

        } else if (isCancelled) {

            primary = dictionaryAndReplace({
                translateFunction,
                dictionaryKey: 'game_canc_reason',
                dictionaryFallback: 'Cancelled: {{reason}}',
                replaceKey: '{{reason}}',
                replaceValue: reason
            });

        } else if (isDelayed) {

            let dictionaryKey = 'game_delay_reason';
            let dictionaryFallback = 'Delayed: {{reason}}';

            if (isPreview) {

                dictionaryKey = 'game_delay_start_reason';
                dictionaryFallback = 'Delayed Start: {{reason}}';

            }

            primary = dictionaryAndReplace({
                translateFunction,
                dictionaryKey,
                dictionaryFallback,
                replaceKey: '{{reason}}',
                replaceValue: reason
            });

        }

        if (!makeup) {

            if (resumedFrom) { // Completion of suspended game

                makeup = dictionaryAndReplace({
                    translateFunction,
                    dictionaryKey: 'game_susp_completion_on_date',
                    dictionaryFallback: 'Completion of game suspended on {{date}}',
                    replaceKey: '{{date}}',
                    replaceValue: formatInterruptionDate(resumedFrom, this._moment)
                });

            } else if (resumeDate) {

                makeup = dictionaryAndReplace({
                    translateFunction,
                    dictionaryKey: 'game_susp_completed_on_date',
                    dictionaryFallback: 'Completed on {{date}}',
                    replaceKey: '{{date}}',
                    replaceValue: formatInterruptionDate(resumeDate, this._moment)
                });

            } else if (rescheduledFrom) { // Previous game date was postponed

                makeup = dictionaryAndReplace({
                    translateFunction,
                    dictionaryKey: 'game_ppd_makeup_of',
                    dictionaryFallback: 'Makeup of {{date}}',
                    replaceKey: '{{date}}',
                    replaceValue: formatInterruptionDate(rescheduledFrom, this._moment)
                });

            }

        }

        if (primary || makeup) {

            return {
                primary,
                makeup
            };

        }

        return null;

    }
    /**
     * Checks to see if it's postseason o tiebreaker game
     * See `getExtendedStatus(statsapiGame)` API docs for details.
     * @param gameType
     * @param tiebreaker
     * @return {boolean}
     * @see getExtendedStatus(statsapiGame)
     */
    getPostSeasonReady(gameType, tiebreaker) {
        const isPostSeason = gameType === 'F'
            || gameType === 'D'
            || gameType === 'L'
            || gameType === 'W'
            || gameType === 'C';

        const isTieBreaker = (tiebreaker && tiebreaker === 'Y')
            && gameType === 'R';

        return {
            isPostSeason,
            isTieBreaker
        };
    }

    /**
     * Convenience method which returns null if `getInterruptedGameStrs(statsapiGame)` returns null, else
     * `getInterruptedGameStrs(statsapiGame).primary` (which is never null).
     * See `getInterruptedGameStrs(statsapiGame)` API docs for details.
     * @param statsapiGame
     * @return {string | null}
     * @see getInterruptedGameStrs(statsapiGame)
     */
    getInterruptedGamePrimaryStr(statsapiGame) {
        let strs = this.getInterruptedGameStrs(statsapiGame);
        return (strs === null ? null : strs.primary);
    }

    /**
     * Convenience method which returns null if `getInterruptedGameStrs(statsapiGame)` returns null, else
     * `getInterruptedGameStrs(statsapiGame).makeup` (which may also be null).  If not null, string will be similar to
     * "Makeup on 7/23" or "Makeup of 5/10".  See `getInterruptedGameStrs(statsapiGame)` API docs for details.
     * @param statsapiGame
     * @return {string | null}
     * @see getInterruptedGameStrs(statsapiGame)
     */
    getInterruptedGameMakeupStr(statsapiGame) {
        let strs = this.getInterruptedGameStrs(statsapiGame);
        return (strs === null ? null : strs.makeup);
    }

    /**
     * @function groupDoubleheaders
     * @param games
     * @description groups games 1 & 2 of doubleheader together
     */
    groupDoubleheaders(games = []) {

        games.forEach((game, gmIndex) => {

            // find games that are game 1 of a doubleheader
            if (game.doubleHeader === 'Y' && game.gameNumber === 1) {
                const gm1Venue = get(game, 'venue.id');
                const gm1Status = get(game, 'status.abstractGameCode');

                // find the index of corresponding game 2, ensure doubleHeader === "Y" to rule out split ticket games
                const gm2Index = findIndex(games, (gm) => {
                    const gmVenue = get(gm, 'venue.id');
                    return (gm.doubleHeader === 'Y' && gm.gameNumber === 2 && gmVenue === gm1Venue);
                });

                // if there is a corresponding game 2 that is not a split ticket,
                // and both games have the same status, move game 2 after game 1
                if (gm2Index > -1) {

                    const game2 = games[gm2Index];
                    const gm2Status = get(game2, 'status.abstractGameCode');

                    if (gm2Status === gm1Status) {

                        const extractedGame2 = games.splice(gm2Index, 1);
                        games.splice(gmIndex, 0, extractedGame2[0]);

                    }

                }
            }

        });

    }

    /**
     * @function groupDoubleheaderGames
     * @param games
     * @description groups games 1 & 2 of doubleheader together besides live games
     */
    groupDoubleheaderGames(games = []) {
        const gamesLength = games.length;

        if (gamesLength > 0) {

            for (let i = 0; i < gamesLength; i++) {

                const gm1Status = get(games[i], 'status.abstractGameCode');

                // find games that are game 1 of a doubleheader
                if (games[i].doubleHeader === 'Y' && games[i].gameNumber === 1) {
                    const gm1Venue = get(games[i], 'venue.id');
                    const gm1Status = get(games[i], 'status.abstractGameCode');
                    let gm2Status;

                    // find the index of corresponding game 2, ensure doubleHeader === "Y" to rule out split ticket games
                    const gm2Index = findIndex(games, (gm) => {
                        const gmVenue = get(gm, 'venue.id');
                        gm2Status = get(gm, 'status.abstractGameCode');
                        const foundMatch = (gm.doubleHeader === 'Y' && gm.gameNumber === 2 && gmVenue === gm1Venue);
                        return foundMatch;
                    });

                    // if there is a corresponding game 2 that is not a split ticket,
                    // and both games have the same status, move game 2 after game 1
                    if (gm2Index > -1 && (gm1Status === 'L' && (gm2Status === 'L' || gm2Status === 'P')) || (gm1Status === 'F' && gm2Status === 'F') || (gm1Status === 'P' && gm2Status === 'P')) {

                        const game2 = games[gm2Index];
                        const gm2Status = get(game2, 'status.abstractGameCode');
                        const extractedGame2 = games.splice(gm2Index, 1);

                        games.splice(i, 0, extractedGame2[0]);

                    }
                }

            }

        }

    }

    /**
     * Get an object with boolean values describing the status of a game using gameType, doubleHeader, status, flags,
     * and content.media.freeGame.  Booleans are:
     * <ul>
     *  <li><b>isTBD: </b>Game time is TBD</li>
     *  <li><b>isFreeGame: </b>This game is free to watch on MLB.TV</li>
     *  <li><b>isAllStar: </b>An all-star game</li>
     *  <li><b>isSpring: </b>A spring-training game</li>
     *  <li><b>isExhibition: </b>An exhibition game</li>
     *  <li><b>isDoubleHeader: </b>Game is part of a double-header (either "classic" or split-ticket)</li>
     *  <li><b>isClassicDoubleHeader: </b>Game is part of a regular (single-ticket) double-header</li>
     *  <li><b>isSplitTicketDoubleHeader: </b>Game is part of a split-ticket double-header (as distinguished from a "classic" double-header)</li>
     *  <li><b>isPreview: </b>Game is in preview (not yet live)</li>
     *  <li><b>isWarmup: </b>The game is in the warmup stage of preview state</li>
     *  <li><b>isLive: </b>The game is in progress</li>
     *  <li><b>isFinal: </b>The game is final</li>
     *  <li><b>isForfeit: </b>One of the teams forfeited the game</li>
     *  <li><b>isNoHitter: </b>Game is a no-hitter</li>
     *  <li><b>isPerfectGame: </b>This is a perfect game</li>
     *  <li><b>isDelayed: </b>The game is delayed (either delayed-start or in-game)</li>
     *  <li><b>isSuspended: </b>The game has been suspended and will be continued on a future date; <code>getInterruptedGameMakeupStr()</code> <i>may</i> supply that date</li>
     *  <li><b>isPostponed: </b>The game has been postponed and will be played on a future date; <code>getInterruptedGameMakeupStr()</code> <i>may</i> supply that date</li>
     *  <li><b>isCancelled: </b>This game is cancelled and will not be played, ever</li>
     *  <li><b>isCompletedEarly:</b> Statsapi game meets this criteria: <code>codedGameState === 'F' && (statusCode !== 'F' && statusCode !== 'FT' && statusCode !== 'FW')</code></li>
     *  <li><b>isManagerChallenge: </b>There is a manager challenge</li>
     *  <li><b>isUmpireReview: </b>Game is in umpire-review</li>
     *  <li><b>isInstantReplay: </b>Officials are reviewing an instant replay</li>
     *  <li><b>isPostSeason: </b>It's a postseason game (not tiebreaker)</li>
     *  <li><b>isTieBreaker: </b>It's a postseason tiebreaker game</li>
     * </ul>
     * @param statsapiGame
     * @returns {{isAllStar: boolean, isCancelled: boolean, isClassicDoubleHeader: boolean, isCompletedEarly: boolean,
     * isDelayed: boolean, isDoubleHeader: boolean, isExhibition: boolean, isFinal: boolean, isForfeit: boolean,
     * isFreeGame: boolean, isInstantReplay: boolean, isLive: boolean, isManagerChallenge: boolean,
     * isNoHitter: boolean, isPerfectGame: boolean, isPostponed: boolean, isPreview: boolean,
     * isSplitTicketDoubleHeader: boolean, isSpring: boolean, isSuspended: boolean, isTBD: boolean,
     * isUmpireReview: boolean, isWarmup: boolean}}
     */
    getExtendedStatus(statsapiGame = {}) {

        const { gameType, tiebreaker, doubleHeader = 'N', status = {}, flags = {} } = statsapiGame;
        const { noHitter, perfectGame } = flags;
        let isFreeGame = false;

        if (statsapiGame.content && statsapiGame.content.media) {

            isFreeGame = !!statsapiGame.content.media.freeGame;

        }

        const { abstractGameCode, codedGameState, statusCode, startTimeTBD } = status;
        const isInGameDelay = (codedGameState === 'I' && (statusCode !== 'I' && statusCode !== 'IH'));
        const isPreGameDelay = (codedGameState === 'P' && (statusCode !== 'P' && statusCode !== 'PW'));
        const completedEarly = (codedGameState === 'F' &&
            (statusCode !== 'F' && statusCode !== 'FT' && statusCode !== 'FW'));
        const { isPostSeason, isTieBreaker } = this.getPostSeasonReady(gameType, tiebreaker);
        const isPostSeasonReady = isPostSeason || isTieBreaker;

        return {
            isAllStar: gameType === 'A',
            isCancelled: codedGameState === 'C',
            isClassicDoubleHeader: doubleHeader === 'Y',
            isCompletedEarly: completedEarly,
            isDelayed: (isInGameDelay || isPreGameDelay),
            isDoubleHeader: doubleHeader !== 'N',
            isExhibition: gameType === 'E',
            isFinal: abstractGameCode === 'F',
            isForfeit: (codedGameState === 'Q' || codedGameState === 'R'),
            isFreeGame: isFreeGame,
            isInstantReplay: statusCode === 'IH',
            isLive: abstractGameCode === 'L',
            isManagerChallenge: codedGameState === 'M',
            isNoHitter: !!noHitter,
            isPerfectGame: !!perfectGame,
            isPostponed: codedGameState === 'D',
            isPreview: abstractGameCode === 'P',
            isSplitTicketDoubleHeader: doubleHeader === 'S',
            isSpring: gameType === 'S',
            isSuspended: (codedGameState === 'T' || codedGameState === 'U'),
            isTBD: !!startTimeTBD,
            isUmpireReview: codedGameState === 'N',
            isWarmup: statusCode === 'PW',
            isPostSeason: isPostSeason,
            isTieBreaker: isTieBreaker,
            isPostSeasonReady: isPostSeasonReady
        };
    }

    /**
    * @function hasTeam
    * @description determine whether a team is involved in a game
    * @param gameData
    * @param teamId Can be either string or int
    * @returns {boolean}
    */
    hasTeam(gameData = {}, teamId = '') {

        if (!teamId) {

            return false;

        }

        const homeId = get(gameData, 'teams.home.team.id');
        const awayId = get(gameData, 'teams.away.team.id');

        // eslint-disable-next-line eqeqeq
        return homeId == teamId || awayId == teamId;

    }

    /**
     * Return The linker class instance which backs this GameUtils instance.  If a consumer is using (for
     * example) both an MlbLinker instance and an MlbGameUtils instance _on the client side_, the consumer should
     * instantiate the MlbGameUtils instance only, and then get an MlbLinker instance using this method. Otherwise,
     * since these packages both use bundled, transpiled files, you'll end up with a significant amount of
     * duplicate code (not a problem on the server side; definitely a problem on the client).
     * @return {Linker} Linker subclass instance
     */
    getBamLinker() {
        return this._linker;
    }

    /**
     * Return The linker class instance using getter
     * @return {Linker} Linker subclass instance
     */
    get linker() {
        return this._linker;
    }

    /**
     * Return the sorted games for postseason push
     * @return {games} sorted games based on divisionLeader && divisionGamesBack
     */
    groupForPostSeasonPush(gameDates = [], postseasonPush = {}, teamID = '') {
        const gameDatesLength = gameDates.length;
        const pspTeams = postseasonPush.ppTeamInclude;
        let postseasonPushGames = [];

        for (let i = gameDatesLength; i--;) {
            const { games } = gameDates[i];

            postseasonPushGames = games.filter((game, index, arr) => {
                const teamHome = get(game, 'teams.home.team.id', null);
                const teamAway = get(game, 'teams.away.team.id', null);
                const hasEitherTeam = (pspTeams.indexOf(teamHome) > -1) || (pspTeams.indexOf(teamAway) > -1);
                game.psPushGame = hasEitherTeam;
                return hasEitherTeam;
            });

            gameDates[i].postseasonPushGames = postseasonPushGames;

        }
    }

    /**
     * Applies sort order to a game
     * @return {games} a game with sorted order
     */
    setSortOrders(games = [], teamID = '', fnCode = []) {

        for (let i = games.length; i--;) {
            games[i].sortOrder = '';

            if (teamID) {

                const hasTeamGame = this.hasTeam(games[i], teamID);
                games[i].sortOrder = (hasTeamGame ? 0 : 1) + '_';

            }

            const extStatus = this.getExtendedStatus(games[i]);
            const fnCodeLength = fnCode.length;

            if (fnCodeLength > 0) {

                for (let j = fnCodeLength; j--;) {
                    const pos = fnCode.indexOf(fnCode[j]);

                    if (fnCode[j] && extStatus[fnCode[j]]) {

                        games[i].sortOrder += pos + '_'
                            + (games[i].status.startTimeTBD ? 1 : 0) + '_'
                            + games[i].gameDate + '_'
                            + games[i].gamePk;

                        break;

                    }

                }

            }

        }
    }

    /**
     * Sorts the games
     * @return {games} sorted games based on abstract game status
     */
    sortGamesLogic(games = []) {
        games.sort(function (a, b) {
            if (a.sortOrder < b.sortOrder) {
                return -1;
            } else if (a.sortOrder > b.sortOrder) {
                return 1;
            } else {
                return 0;
            }
        });
    }

    /**
     * Return the sorted games
     * @return {games} sorted games based on abstract game status
     */
    sortByGameStatus(gameDates = [], teamID = '', fnCode = []) {
        const gameDatesLength = gameDates.length;

        if (gameDatesLength > 0) {

            for (let dl = gameDatesLength; dl--;) {
                const { games } = gameDates[dl];

                this.setSortOrders(games, teamID, fnCode);
                this.sortGamesLogic(games);
                this.groupDoubleheaderGames(games);

            }

        }
    }

    /**
     * @function sortGames
     * @description For each date, sort the games by their status in STATUS_ORDER
     * then by gameDate value
     * @param mlbMoment
     * @param extendedData
     * @param teamId
     * @param STATUS_ORDER
     */
    sortGames(mlbMoment, extendedData = {}, teamId = '', STATUS_ORDER = {}) {

        const { dates } = extendedData;

        dates.forEach((date) => {

            const { games } = date;

            games.sort((a, b) => {

                // If club context, sort games with that team to top
                if (teamId) {

                    const hasTeamGameA = this.hasTeam(a, teamId);
                    const hasTeamGameB = this.hasTeam(b, teamId);

                    if (hasTeamGameA && !hasTeamGameB) {

                        return -1;

                    } else if (hasTeamGameB && !hasTeamGameA) {

                        return 1;

                    }

                }

                const statusA = a.status.abstractGameCode;
                const statusB = b.status.abstractGameCode;
                const tbdA = a.status.startTimeTBD;
                const tbdB = b.status.startTimeTBD;

                let retCode = STATUS_ORDER[statusA][statusB];

                if (retCode === 0) {

                    if (tbdA || tbdB) {

                        // if either game is TBD sort that game after
                        if (tbdA && !tbdB) {

                            retCode = 1;

                        } else if (tbdB && !tbdA) {

                            retCode = -1;

                        } else {

                            retCode = 0;

                        }

                    } else {

                        // if neither game is TBD sort by game time
                        const moment = mlbMoment.getMomentTimezone();
                        const dateA = a.gameDate;
                        const dateB = b.gameDate;

                        if (moment(dateA).isBefore(dateB)) {

                            retCode = -1;

                        } else if (moment(dateA).isAfter(dateB)) {

                            retCode = 1;

                        }

                    }

                }

                return retCode;

            });

            this.groupDoubleheaders(games);

        });
    }


}

export default GameUtils;
